import { memo, type SyntheticEvent } from "react";
import * as Yup from "yup";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import type { Option, ResourceResponse } from "types";
import { useTypedSelector, useForm } from "hooks";
import { getFormInputProps, isFormChanged } from "util/forms";
import { selectDomainOptions } from "features/setups/selectors";
import { useWaTemplatesSetupsActions } from "features/waMessaging/actions";
import type { WaTemplateSetup } from "features/waMessaging/types";
import Autocomplete from "ui/atoms/Autocomplete";
import ActionPanel from "ui/molecules/ActionPanel";
import { useWaTemplateSetupContext } from "./WaTemplateSetupProvider";

type FormValues = {
    readonly MOR: Option | null;
    readonly messageTemplate: string;
};

const validationSchema = Yup.object({
    MOR: Yup.object().nullable().required('Domain is required'),
    messageTemplate: Yup.string().trim().required('Template is required')
});

const getInitialValues = (
    resource: WaTemplateSetup | undefined,
    domains: Array<Option>
): FormValues => {
    if (!resource) {
        return {
            MOR: null,
            messageTemplate: ''
        };
    }

    return {
        MOR: domains.find((option: Option) => option.id === resource.MOR) ?? null,
        messageTemplate: resource.messageTemplate ?? ''
    };
};

type Props = {
    readonly resource: WaTemplateSetup | undefined;
};

const WaTemplateSetupForm = ({ resource }: Props) => {
    const domains = useTypedSelector(selectDomainOptions);
    const { onClose } = useWaTemplateSetupContext();
    const {
        createWaTemplatesSetup,
        updateWaTemplatesSetup
    } = useWaTemplatesSetupsActions();

    const requestHandler = resource
        ? updateWaTemplatesSetup
        : createWaTemplatesSetup;

    const form = useForm<FormValues>({
        initialValues: getInitialValues(resource, domains),
        validationSchema,
        enableReinitialize: false,
        onSaveOrCreate: async (values) => {
            const body: Partial<WaTemplateSetup> = {
                ...(resource ? { coreId: resource.coreId } : {}),
                MOR: values.MOR!.id as string,
                messageTemplate: (values.messageTemplate ?? '').trim()
            };

            const [request, result] = requestHandler(body);
            const response = await request as ResourceResponse<WaTemplateSetup>;

            if (response?.success) {
                onClose();
            }

            return result;
        }
    });

    const onAutocompleteChange = (fieldName: keyof FormValues) =>
        (_: SyntheticEvent, value: Option | Option[] | null) => {
            form.setFieldValue(fieldName, value, true);
            setTimeout(() => form.setFieldTouched(fieldName, true));
        };

    return (
        <Box
            component='form'
            onSubmit={form.handleSubmit}
            autoComplete='off'
        >
            <Box
                sx={{
                    display: 'grid',
                    alignItems: 'flex-start',
                    gridTemplateColumns: '1fr',
                    gap: { xs: 2, sm: 3 }
                }}
            >
                <Autocomplete
                    fullWidth
                    size='small'
                    layout={null}
                    options={domains}
                    disabled={Boolean(resource)}
                    getOptionLabel={(option: Option) => option.name}
                    isOptionEqualToValue={(a: Option, b: Option) => a.id === b.id}
                    value={form.values.MOR}
                    onChange={onAutocompleteChange('MOR')}
                    TextFieldProps={{
                        ...getFormInputProps('MOR', form),
                        required: true,
                        label: 'Domain'
                    }}
                />
                <TextField
                    {...getFormInputProps('messageTemplate', form)}
                    fullWidth
                    required
                    multiline
                    minRows={6}
                    size='small'
                    name='messageTemplate'
                    label='Template'
                    value={form.values.messageTemplate}
                    onChange={form.handleChange}
                    onBlur={form.handleBlur}
                />
            </Box>
            <ActionPanel
                isLoading={form.isSubmitting}
                onSave={async () => { }}
                onCancel={onClose}
                SaveActionProps={{
                    type: 'submit',
                    disabled: !form.isValid || (Boolean(resource) && !isFormChanged(form)),
                    loading: form.isSubmitting,
                    children: resource ? 'Save' : 'Create'
                }}
            />
        </Box>
    );
};

export default memo(WaTemplateSetupForm);
